import { XMarkIcon } from '@heroicons/react/20/solid';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/navigation';
import { FC, useCallback, useEffect, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { FieldValues, useForm } from 'react-hook-form';
import { FaSearch } from 'react-icons/fa';
import { useContextSelector } from 'use-context-selector';

import { fetchContacts } from '@/core/apis/content-apis';
import { sharedSpaceCreateThread, updateThread } from '@/core/apis/share-space-api';
import { CommandPaletteVendorSelector } from '@/core/store/command-palette/command-palette-states';
import { CommandPaletteContext } from '@/core/store/command-palette/CommandPaletteProvider';
import { Authentication } from '@/core/store/global/app-context-types';
import { authSelector } from '@/core/store/global/app-states';
import { AppContext } from '@/core/store/global/AppProvider';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';
import { usePagination } from '@/core/utils/usePagination';
import { appSettings } from '@/settings/app-settings';
import Loader from '@/ux-legacy/components/common/Loader/Loader';
import { Contact } from '@/ux-legacy/components/send/send-types';
import { LoadingCircleIcon } from '@/ux-legacy/uikit/iconset/iconset-index';
import { IMG_PLACEHOLDER } from '@/ux-legacy/uikit/utils/img-placeholder';

import { Vendor } from '../common/bentoboard/Common/board-types';
import { DatePickerWithRange } from '../common/DateRangePicker/DateRangePicker';
type CreatePortalFormProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  business?: any;
  createdPortal?: (data: any) => void;
  updatedThread?: (data: any) => void;
  navigate?: boolean;
  userList?: any[];
  existingThread?: any;
  returningMessageMessage?: (data: any) => void;
};

const LIMIT = 20;

const CreatePortalForm: FC<CreatePortalFormProps> = ({
  show,
  setShow,
  business = undefined,
  createdPortal = () => {},
  navigate = true,
  userList,
  existingThread,
  updatedThread = () => {},
  returningMessageMessage = () => {},
}) => {
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isFetchLoading, setIsFetchLoading] = useState(false);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const authentication: Authentication = useContextSelector(AppContext, authSelector);
  const vendors: { owner?: Vendor; employee?: Vendor[] } = useContextSelector(
    CommandPaletteContext,
    CommandPaletteVendorSelector,
  );
  const [currentPage, setCurrentPage] = useState('details');
  const [selectedDate, setSelectedDate] = useState<DateRange | undefined>();

  const router = useRouter();
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    reset();
  }, [show]);

  useEffect(() => {
    if (userList && userList?.length > 0) {
      const userListUpdate: any = userList.map((user) => ({
        name: user?.Name,
        email: user?.Email,
        displayPic: user?.DisplayPic,
        handle: user?.Handle,
        _id: user?.Id,
        destinationId: user?.Id,
      }));

      setSelectedContacts([...selectedContacts, ...userListUpdate]);
    }
  }, [userList]);

  const onCreate = (data: FieldValues) => {
    setIsCreating(true);
    const vendorId = business?._id;
    const userIds = selectedContacts.map((user) => user.destinationId);

    {
      const payload: any = {
        threadName: data?.Name,
        message: data?.Message,
        vendorId: vendorId,
        userIds: userIds,
      };

      if (selectedDate?.from && selectedDate?.to) {
        payload.startDate = format(selectedDate.from, 'yyyy-MM-dd'); // Format to "2024-11-13"
        payload.endDate = format(selectedDate.to, 'yyyy-MM-dd'); // Format to "2024-11-13"
      }

      sharedSpaceCreateThread(payload)
        .then((res) => {
          if (navigate) {
            router.push(`/thread/${res.data.thread._id}`);
          } else {
            const newMessage = {
              ...res.data.message,
              reactions: [],
              userInfo: {
                _id: authentication.userId,
                Name: authentication.profileData?.fullName,
                Email: authentication.profileData?.email,
                DisplayPic: authentication.masterProfile?.DisplayPic,
                Handle: authentication.masterProfile?.Handle,
                // Role: userRole,
              },
              numberOfReplies: 0,
            };
            returningMessageMessage(newMessage);
          }
          setShow(false);
        })
        .finally(() => {
          setIsCreating(false);
        });
    }
  };

  useEffect(() => {
    if (!existingThread && !business && selectedContacts?.length === 1) {
      setValue('Name', `Space with the ${selectedContacts[0]?.name || selectedContacts[0]?.handle}`);
    }
  }, [selectedContacts]);

  const onUpdate = (data: FieldValues) => {
    setIsCreating(true);
    const vendorId = business?._id;
    const userIds = selectedContacts.map((user) => user.destinationId);

    {
      const payload: any = {
        threadName: data?.Name,
        message: data?.Message,
      };

      if (selectedDate?.from && selectedDate?.to) {
        payload.startDate = format(selectedDate.from, 'yyyy-MM-dd'); // Format to "2024-11-13"
        payload.endDate = format(selectedDate.to, 'yyyy-MM-dd'); // Format to "2024-11-13"
      }

      updateThread(existingThread?._id, payload)
        .then((res) => {
          updatedThread(res?.data?.message);
          setShow(false);
        })
        .finally(() => {
          setIsCreating(false);
        });
    }
  };

  const loadMore = () => {
    fetchBusinessContacts(LIMIT + offset);
    setCurrentOffset(LIMIT + offset);
  };

  const { count, offset, moreElement, setCurrentCount, setCurrentOffset } = usePagination(loadMore, 0);

  const fetchBusinessContacts = (offset: number, searchString?: string) => {
    setIsFetchLoading(true);
    const destinationType = userList && userList.length > 0 ? 'USER' : undefined;
    fetchContacts(appSettings.api, {
      limit: LIMIT,
      offset: offset,
      belongsTo: userList && userList?.length > 0 ? 'USER' : 'BUSINESS',
      search: searchString,
      vendorId: business?._id,
      destinationType,
    })
      .then((res) => {
        setContacts([...contacts, ...res.contacts]);
        setCurrentCount([...contacts, ...res.contacts].length);
        setHasMore([...contacts, ...res.contacts].length < res.total);
        setShowSearchResults(true);
      })
      .finally(() => {
        setIsFetchLoading(false);
        setShowSearchResults(true);
      });
  };

  const onToggleStoreFrontItemSelect = (contact: Contact) => {
    if (selectedContacts?.findIndex((cont: Contact) => cont?.destinationId == contact?.destinationId) > -1) {
      setSelectedContacts(
        selectedContacts?.filter((cont: Contact) => cont?.destinationId != contact?.destinationId),
      );
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const fetchContactSearch = (searchString: string) => {
    setHasMore(false);
    setContacts([]);
    if (searchString) {
      fetchBusinessContacts(0, searchString);
    }
  };

  const debouncedSearch = useCallback(debounce(fetchContactSearch, 750), []);

  const onSubmit = () => {
    if (existingThread) {
      handleSubmit(onUpdate)();
    } else {
      handleSubmit(onCreate)();
    }
  };

  const handleDateSelection = (date: DateRange | undefined) => {
    setSelectedDate(date);
  };

  useEffect(() => {
    if (existingThread) {
      setValue('Name', existingThread.threadName || '');
      console.log('existingThread', existingThread);
      const textMessage = existingThread?.contents?.filter((content: any) => content?.contentType === 'text');
      setValue('Message', existingThread?.message || textMessage[0]?.content?.text);
    }
  }, [existingThread]);

  return (
    <>
      {show && (
        <button
          // onClick={() => {
          //   setShow(false);
          //   setCurrentPage('details');
          // }}
          className="fixed top-0 left-0 w-full h-full flex z-[9999] bg-slate-800/50"
        ></button>
      )}

      <div
        className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90dvw] md:w-[40dvw] max-h-[85dvh] h-auto p-4 z-[9999] bg-white rounded-lg border border-black border-opacity-20 flex flex-col gap-y-2 ${
          show ? 'scale-100 opacity-100' : 'scale-90 opacity-0 pointer-events-none'
        }`}
      >
        <div className="w-full flex flex-row justify-between py-2">
          <div></div>
          <div className=" text-xl font-bold text-slate-800">
            {existingThread ? 'Update' : 'Create'} Space
          </div>
          <XMarkIcon
            className="h-7 w-7 text-slate-800 hover:bg-slate-200 rounded-full "
            onClick={() => {
              document.body.style.overflow = 'auto';
              setShow(false);
              setCurrentPage('details');
            }}
          />
        </div>

        <div className="flex flex-wrap w-full gap-1 h-auto overflow-y-auto my-1">
          {selectedContacts.map((contact, index) => {
            return (
              <div
                key={contact._id}
                className="p-2 flex w-fit h-fit items-center gap-x-2 rounded-md bg-slate-800 text-white"
              >
                <span> {contact.name || contact.email.split('@')[0]}</span>
                <XMarkIcon
                  className="h-4 w-4 rounded-full bg-slate-600 cursor-auto"
                  onClick={() => {
                    setSelectedContacts((curr) =>
                      curr.filter((selectedContact) => selectedContact._id != contact._id),
                    );
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="w-full flex relative">
          {!existingThread && (
            <div className="w-full flex py-3">
              <div className="w-full flex flex-col relative">
                <FaSearch className="h-4 w-4 text-slate-400 absolute top-1/2 -translate-y-1/2 translate-x-2" />
                <input
                  placeholder="Search Contacts"
                  className="w-full pr-4 pl-8 py-2 outline-none border-none bg-gray-200 rounded-full"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={(e) => {
                    debouncedSearch(e.target.value);
                    setSearchQuery(e.target.value);
                  }}
                ></input>
              </div>

              {searchQuery ? (
                <button
                  className="w-[100px] flex justify-end items-center "
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setSearchQuery('');
                    setShowSearchResults(false);
                  }}
                >
                  Cancel
                </button>
              ) : (
                <button
                  className="w-[100px] flex justify-end items-center "
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowSearchResults(false);
                    setSelectedContacts([]);
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
          )}
          {showSearchResults && (
            <div className="absolute top-[105%] left-0 flex flex-col gap-y-1 bg-white border shadow w-full max-h-[300px] overflow-y-auto rounded-md">
              {contacts?.length > 0 && (
                <>
                  <div className="flex flex-col">
                    {contacts?.map((contact: any, index: number) => {
                      return (
                        <button
                          className={`flex flex-row items-center gap-2 p-1 relative ${
                            index != 0 ? 'border-t' : ''
                          }`}
                          key={contact?._id}
                          onClick={() => onToggleStoreFrontItemSelect(contact)}
                        >
                          {selectedContacts?.findIndex(
                            (cont: Contact) => contact?.destinationId == cont.destinationId,
                          ) > -1 && (
                            <CheckCircleIcon className="text-green-400 h-6 w-6 absolute top-2 right-2" />
                          )}
                          <img
                            alt="user_pic"
                            src={
                              contact?.displayPic
                                ? appendImageRepoDomainIfNotExistInUrl(contact?.displayPic)
                                : IMG_PLACEHOLDER
                            }
                            className="rounded-full border-2 border-white w-10 h-10 grow-0"
                          />
                          <div className="grow">
                            <div className="text-gray-500 text-left truncate">{contact?.name}</div>
                            <div className="text-gray-400 text-left text-sm truncate">{contact?.email}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
              {!isFetchLoading && hasMore && (
                <div ref={moreElement} className="w-full h-[8px] bg-white"></div>
              )}
              {!isFetchLoading && contacts?.length <= 0 && (
                <div className="text-center">Contacts not found</div>
              )}
              {isFetchLoading && (
                <div className="w-full flex justify-center mt-4">
                  <Loader />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-full h-full flex flex-col gap-y-2 overflow-auto pb-4">
          <div className="w-full h-full flex flex-col gap-y-0.5">
            <div className="w-[200px] font-[600] text-[15px] flex-shrink-0">Name</div>
            <div className="w-full flex flex-col">
              <input
                type="text"
                id="firstNameInput"
                placeholder="Enter Name of the Space"
                autoComplete="off"
                {...register('Name', {
                  required: 'Name is required',
                  validate: (value) => {
                    return value.trim() !== '' && !value.startsWith(' ');
                  },
                })}
                maxLength={50}
                className={`border p-3 rounded-[12px] text-gray-800 w-full text-[15px] ${
                  errors.Name ? 'border-red-500' : 'border-gray-200'
                }`}
              />
              <p className="flex justify-between text-slate-400">
                <div>
                  {errors.Name && (
                    <span className="text-red-500">{`Name can't be empty or start with space.`}</span>
                  )}
                </div>
                {watch('Name')?.length || 0}/50
              </p>
            </div>
          </div>

          {/* Message */}
          {/* <div className="w-full h-full flex flex-col gap-y-1">
            <div className="w-[200px] font-[600] flex-shrink-0">Message</div>
            <textarea
              id="message"
              placeholder="Enter Message"
              autoComplete="off"
              spellCheck="false"
              {...register('Message', {
                required: 'Message is required',
                validate: (value) => {
                  return value.trim() !== '' && !value.startsWith(' ');
                },
              })}
              // {...register('Message')}
              rows={3}
              maxLength={250}
              className={`border border-gray-200 p-3  rounded-[12px] text-gray-800 w-full focus:outline-none focus:ring-0 focus:border-gray-200 text-[15px] resize-none`}
            />

            <p className="flex justify-between text-slate-400">
              <div>
                {errors.Message && (
                  <span className="text-red-500">{`Message can't be empty or start with space.`}</span>
                )}
              </div>
              {`${watch('message')?.length || 0}/250`}
            </p>
          </div> */}
          {selectedContacts?.length > 1 && (
            <div className="w-full h-full flex flex-col gap-y-1">
              <div className="w-[200px] font-[600] flex-shrink-0">Select Date</div>
              <DatePickerWithRange
                onDateSelect={handleDateSelection}
                exisitingDate={
                  selectedDate
                    ? undefined
                    : { start: existingThread?.startDate, end: existingThread?.endDate }
                }
                selectableRange={'future'}
              />
            </div>
          )}
        </div>

        <div className={`w-full flex ${currentPage === 'details' ? 'justify-end' : 'justify-between'}`}>
          <button
            onClick={onSubmit}
            className={` text-white py-2 rounded-full ${
              isCreating ? 'px-6 bg-gray-500 pointer-events-none' : ' bg-black px-4'
            }`}
          >
            {isCreating ? (
              <LoadingCircleIcon classNames="w-6 h-6 text-white" />
            ) : existingThread ? (
              'Update'
            ) : (
              'Create'
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default CreatePortalForm;
